import React, { useState, useEffect, useContext, useRef, useCallback } from 'react'
import Image from 'next/image'
import { UserContext } from '../../context/UserContext'
import { s3Static } from '../../utils/constants'
import { addVisitCount, downloadAbout, downloadProfilePic } from '../../api/user/profile-api'
import { LoadingCover } from '../commons/LoadingCover'
import { SystemError } from '../error/SystemError'
import { AnimatePresence, motion } from 'framer-motion'
import { openModal } from '../../service/modal-service'
import { ImageModal } from '../commons/ImageModal'
import { uploadImage } from '../../api/user/post-api'
import { openImageHandler } from '../../js/open-image'
import { acceptString, addString } from '../../api/connections/connections-api'
import { shareContent } from '../../service/share-content'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { ShareFallbackModal } from '../commons/ShareFallbackModal'
import { 
    Share2, UserPlus, UserCheck, Clock, Home, Eye, 
    ShieldCheck, Camera, Star, Briefcase,
    ShieldOff
} from 'lucide-react'
import { RateService } from './RateService'

export const ProfileHeader = (props) => {
    const stringHandleName = props.stringHandleName
    const stringsUserId = props.stringsUserId
    const profilePicture = props.profilePicture
    const router = useRouter()
    
    const file = useRef('')
    const inputFile = useRef(null);
    
    const [ about, setAbout ] = useState(props.about);
    const [ isOpenMedia, setIsOpenMedia] = useState(false)
    const [ profileMedia, setProfileMedia ] = useState(undefined)
    const [ profileMediaType, setProfileMediaType ] = useState("")
    const [ imageX, setImageX ] = useState(0)
    const [ imageY, setImageY ] = useState(0)
    const [ showShareModal, setShowShareModal ] = useState(false)
    const [ shareData, setShareData ] = useState(null)
    const [ isShowRateService, setIsShowRateService ] = useState(false)

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}); 

    const { userId, userProfilePicture, setUserProfilePicture, businessType } = useContext(UserContext)

    const changeRoute = (path) =>{
        setIsLoading(true)
        router.push(path).then(() => {
            setIsLoading(false)
        })
    }

    const addViewCount = useCallback(async() => {
        try {
            addVisitCount(stringsUserId)
        } catch (error) {
            console.log(error);
            return
        }
    }, [stringsUserId])

    useEffect(() => {
        if(about.professionalType != "Professional") return
        addViewCount()
    }, [about.professionalType, addViewCount])

    const getAbout = useCallback(async() => {
        if(!stringsUserId) return
        setIsLoading(true)
        try {
            if(!userId){
                setAbout(await downloadAbout("", stringsUserId))
            } else {
                setAbout(await downloadAbout(userId, stringsUserId))
            }
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setErrorContent({'header': "Error getting profile info", 'body': "Please reload the app and try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            return
        }
    }, [userId, stringsUserId])
    

    useEffect(() => {
      if(!stringsUserId) return
      getAbout()
      addViewCount()
    }, [stringsUserId, getAbout, addViewCount])
    
    const connectString = async() => {
        if(!userId){
            changeRoute("/signin?redirect=" + router.asPath)
            return
        }
        setIsLoading(true)
        try {            
            await addString(userId, stringsUserId)
            getAbout()
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setErrorContent({'header': "Error sending connection request", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            return
        }
    }

    const accept = async() => {
        if(!userId){
            changeRoute("/signin?redirect=" + router.asPath)
            return
        }
        setIsLoading(true)
        try {
            await acceptString(userId, stringsUserId)
            setIsLoading(false)
            getAbout()
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setErrorContent({'header': "Unable to accept connection request", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            return
        }
    }

    const getProfilePicture = async() => {
        try {
            setUserProfilePicture(await downloadProfilePic(userId))
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Error getting profile picture", 'body': "Please reload the app and try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            return
        }
    }

    const uploadProfileImage = async() => {
        try {
            await uploadImage(file.current, userId, about.userFirstName + " " + about.userLastName, "Profile Picture", "Profile", "Public", "strings", "My new Profile Picture")
            getProfilePicture()
        } catch (error) {
            console.log(error);
            setErrorContent({'header': "Error uploading profile picture", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            return;
        }
    }

    const handleFileChange =(e) => {
        if(e.target.files.length == 0) return;
        let files = e.target.files;
        file.current = files[files.length-1];        
        var reader = new FileReader();
        if(file.current.size > 10485760){
            setErrorContent({'header': "File size exceeded", 'body': "Please try again with a file size less than 10MB."});
            setIsSystemError(true);
            return;
        } 
        uploadProfileImage()        
    }  

    const share = async () => {
        const title = `${about.userFirstName} ${about.userLastName} | ${about.category} | StringsSG`
        const url = window.location.origin + "/profile/" + stringHandleName
        const result = await shareContent(title, url)
        if (!result.success && result.fallbackData) {
            setShareData(result.fallbackData)
            setShowShareModal(true)
        }
    }

    const openImage = (event, media, mediaType) => {
        openImageHandler(event, setImageX, setImageY)  
        setProfileMedia(media);
        setProfileMediaType(mediaType)
        openModal(setIsOpenMedia);    
    }

    const getRatingColor = (strength) => {
        const colors = {
            success: 'bg-green-500',
            primary: 'bg-blue-500',
            info: 'bg-cyan-500',
            warning: 'bg-amber-500',
            danger: 'bg-red-500',
            default: 'bg-gray-400'
        }
        return colors[strength] || colors.default
    }

    const getCategoryDisplay = () => {
        if (about.category === "Fitness Trainer") {
            if(about.isOnboarded) return "StringsSG Personal Trainer"
            return about.professionalType === "Business" ? "Personal Training Service" : "Freelance Trainer"
        }
        return about.category
    }

  return (
    <>  
        <div className='px-4 -mt-12 relative z-10'>
            {/* Profile Card */}
            <div className='bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden'>
                <div className='p-4'>
                    {/* View Count (own profile) */}
                    {(userId === stringsUserId && about.viewCount) &&(
                        <div className='flex justify-end mb-2'>
                            <div className='flex items-center gap-1 text-xs text-gray-400'>
                                <Eye size={14} />
                                <span>{about.viewCount} views</span>
                            </div>
                        </div>
                    )}

                    {/* Deactivated Notice */}
                    {!about && (
                        <div className='text-gray-400 text-center py-4'>Deactivated Profile</div>
                    )}

                    {/* Profile Info Row */}
                    <div className='flex gap-4'>
                        {/* Profile Picture */}
                        <div className='shrink-0'>
                            {userId === stringsUserId ? (
                                <>  
                                    {userProfilePicture && (                                          
                                        <div className='relative w-24 h-24 group' 
                                                onClick={() => inputFile.current.click()}>
                                            <input className='hidden'
                                                type="file" name="photo"
                                                accept="image/*"
                                                capture={false}
                                                onChange={handleFileChange}
                                                ref={inputFile}
                                            />
                                            <Image 
                                                className='rounded-2xl cursor-pointer' 
                                                src={userProfilePicture}
                                                fill
                                                style={{objectFit: 'cover'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='profile image'
                                            />
                                            <div className='absolute inset-0 bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                                                <Camera size={24} className='text-white' />
                                            </div>
                                        </div>                                                    
                                    )}         
                                </>
                            ) : (
                                <>
                                    {profilePicture && (
                                        <div 
                                            className='relative w-24 h-24 cursor-pointer'
                                            onClick={(event) => openImage(event, profilePicture, "image/jpeg")}
                                        >
                                            <Image 
                                                className='rounded-2xl'
                                                src={profilePicture}
                                                fill
                                                style={{objectFit: 'cover'}}
                                                placeholder='blur'
                                                blurDataURL={s3Static + "placeholder.png"}
                                                alt='profile image'
                                            />                                
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Name & Info */}
                        <div className='flex-1 min-w-0'>
                            <h1 className='text-lg font-bold text-gray-900 truncate'>
                                {about.userFirstName} {about.userLastName}
                            </h1>
                            {/* Employee Badge */}
                            {(about.userAccountType === "Employee" || about.userAccountType === "Professional") && (
                                <>
                                    {about.rating > 0 &&     
                                        <Link 
                                            href={`/profile/${stringHandleName}/reviews`} 
                                            className='inline-flex items-center gap-2 my-1'
                                        >
                                            <span className={`text-white text-xs px-2 py-0.5 rounded-full font-medium ${getRatingColor(about.ratingStrength)}`}>
                                                {about.rating?.toFixed(1)}
                                            </span>
                                            <div className="flex gap-0.5">
                                                {[1,2,3,4,5].map((star) => (
                                                    <Star 
                                                        key={star} 
                                                        size={14} 
                                                        className={`${star <= Math.round(about.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                                    />
                                                ))}
                                            </div>
                                        </Link>
                                    }
                                    <div className='flex items-center gap-1 mt-1'>
                                        <Briefcase size={14} className='text-gray-400' />
                                        <span className='text-sm text-gray-600'>{getCategoryDisplay()}</span>
                                    </div>
                                    {(about.isOnboarded && about.userActiveInd === "Y") ?
                                        <div className='inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium'>
                                            <ShieldCheck size={14} />
                                            Verified
                                        </div>
                                        :
                                        <div className='inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-medium'>
                                            <ShieldOff size={14} />
                                            Unverified
                                        </div>
                                    }
                                </>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className='flex gap-2 mt-4 pt-4 border-t border-gray-100'>
                        {/* Connection Button */}
                        {(userId != stringsUserId && businessType != "job") && (
                            <>
                                {about.connectionStatus === "None" && (
                                    <motion.button 
                                        whileTap={{ scale: 0.95 }}
                                        onClick={connectString}
                                        className='text-sm md:text-base flex-1 flex items-center justify-center gap-2 py-2.5 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 transition-colors'
                                    >
                                        <UserPlus size={16} />
                                        Connect
                                    </motion.button>
                                )}
                                {about.connectionStatus === "Pending" && (
                                    <div className='text-sm md:text-base flex-1 flex items-center justify-center gap-2 py-2.5 bg-gray-100 text-gray-500 font-medium rounded-xl'>
                                        <Clock size={16} />
                                        Pending
                                    </div>
                                )}
                                {about.connectionStatus === "Connected" && (
                                    <div className='text-sm md:text-baseflex-1 flex items-center justify-center gap-2 py-2.5 bg-green-100 text-green-700 font-medium rounded-xl'>
                                        <UserCheck size={16} />
                                        Connected
                                    </div>
                                )}
                                {about.connectionStatus === "Accept" && (
                                    <motion.button 
                                        whileTap={{ scale: 0.95 }}
                                        onClick={accept}
                                        className='text-sm md:text-baseflex-1 flex items-center justify-center gap-2 py-2.5 bg-green-500 text-white font-medium rounded-xl hover:bg-green-600 transition-colors relative'
                                    >
                                        <UserPlus size={16} />
                                        Accept
                                        <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                        </span>
                                    </motion.button>
                                )}
                            </>
                        )}

                        {/* Share Button */}
                        <motion.button 
                            whileTap={{ scale: 0.95 }}
                            onClick={share}
                            className={`${(userId != stringsUserId && businessType != "job") ? '' : 'flex-1'} text-sm md:text-base flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors`}
                        >
                            <Share2 size={16} />
                            Share
                        </motion.button>
                    </div>
                </div>
            </div>
        </div>

        {isLoading && <LoadingCover/>}
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        <AnimatePresence>
            {isOpenMedia && (
                <ImageModal media={profileMedia} imageX={imageX} imageY={imageY} mediaType={profileMediaType}/>
            )}
        </AnimatePresence>
        {/* Rate Service Modal */}
        {isShowRateService && (
            <RateService stringsUserId={stringsUserId} about={about} />
        )}
        <ShareFallbackModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            shareData={shareData}
        />
    </>
  )
}
